import Axios from 'axios'
import { createBrowserHistory, History } from 'history'
import merge from 'lodash/merge'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { BrowserRouter } from 'react-router-dom'
import logger from 'redux-logger'
import { Persistor, persistStore } from 'redux-persist'
import { PersistGate } from 'redux-persist/integration/react'

import 'normalize.css'
import 'styles/main.scss'

import rootReducer, { State } from './redux/ducks'

import AppRoutes from 'admin/components/routes'
import defaultConfig from 'admin/config/main'
import * as lang from 'admin/lang/sv'
import Api from 'admin/services/api'
import Authenticator from 'services/authenticator'
import Locale from 'services/locale'
import StorageHelper from 'services/storageHelper'

import { Action, configureStore, Store } from '@reduxjs/toolkit'

import { ConfigType } from 'types'


declare const isDevelopment: boolean
declare const useMockedApi: boolean
declare const module: any

Axios.get('/appversion.json', { validateStatus: undefined }).then((result) => {
  const { major, minor, patch } = result.data.version
  const version = `${major}.${minor}.${patch}`
  // @ts-ignore
  window.appversion = version
  console.log(`version: ${version}`)
})

Promise.all([
  Axios.get('/admin/config.admin.json', { validateStatus: undefined })
    .then((result) =>
      result.status >= 200 && result.status < 300 ? result.data : {}
    )
    .catch(() => {
      return {}
    }),
]).then(([localConfig]) => {
  let config = merge({}, defaultConfig, localConfig) as ConfigType

  if (isDevelopment) {
    if (useMockedApi) {
      config = merge({}, config, {
        api: 'http://localhost:3000',
        keycloak: { url: 'http://localhost:3000/auth' },
      })
    }
  }

  const browserHistory = createBrowserHistory({
    basename: config.basePath,
  })
  const storage = new StorageHelper(localStorage)
  const authenticator = new Authenticator(
    { ...config.keycloak, basePath: config.basePath },
    storage
  )
  const api = new Api(config, browserHistory, authenticator)
  const locale = new Locale(config.lang, lang.translations, lang.options)
  const preloadedState: Partial<State> = {
    featureToggle: {
      isLoading: false,
      error: null,
      data: config.featureToggle,
    },
  }
  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        thunk: {
          extraArgument: { api },
        },
        serializableCheck: false,
      }).concat(isDevelopment ? [logger] : []),
    devTools: isDevelopment,
  })

  const persistor = persistStore(store, null, () => store.getState())
  const reactDependencies = { config, locale, browserHistory }

  if (isDevelopment) {
    console.log('useMockedApi', useMockedApi)
    if (module.hot) {
      console.log('Hot module reload', module.hot.status())
      module.hot.accept('./components/routes', () => {
        const NextComponent = require('./components/routes').default
        renderApp(NextComponent, reactDependencies, store, persistor)
      })
      module.hot.accept('./redux/ducks', () => {
        const nextRootReducer = require('./redux/ducks').default
        store.replaceReducer(nextRootReducer)
      })
    }
  }

  renderApp(AppRoutes, reactDependencies, store, persistor)
})

type Dependencies = {
  config: ConfigType
  locale: Locale
  browserHistory: History
}

const renderApp = (
  component: React.ComponentType<any>,
  dependencies: Dependencies,
  store: Store<State, Action>,
  persistor: Persistor
) => {
  const root = createRoot(document.getElementById('app') as HTMLElement)
  root.render(
    <Provider store={store}>
      <PersistGate persistor={persistor}>
        <BrowserRouter basename={dependencies.config.basePath}>
          {React.createElement(component, dependencies)}
        </BrowserRouter>
      </PersistGate>
    </Provider>
  )
}
